/**
 * Navigate to the editor for an existing Uploads / My Designs library item.
 * Does not treat the item as a fresh upload.
 */
import { clearEditorSession } from "../state/editorStore";

function positiveId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function libraryItemCreationId(item) {
  if (!item) return null;
  if (item.kind === "export") return null;
  if (item.kind === "upload") {
    return (
      positiveId(item.root_creation_id) ??
      positiveId(item.creation_id) ??
      positiveId(String(item.id).replace(/^upload:/, ""))
    );
  }
  return (
    positiveId(item.creation_id) ??
    positiveId(item.id_numeric) ??
    positiveId(String(item.id).replace(/^creation:/, ""))
  );
}

export function libraryItemExportId(item) {
  if (!item || item.kind !== "export") return null;
  return (
    positiveId(item.export_id) ??
    positiveId(String(item.id).replace(/^export:/, ""))
  );
}

export function openLibraryItemInEditor(navigate, item, { setNormalEntry, clearImage } = {}) {
  if (!navigate || !item) return;

  clearEditorSession();
  setNormalEntry?.();
  clearImage?.();

  const exportId = libraryItemExportId(item);
  if (exportId) {
    navigate(`/editor?exportId=${exportId}`);
    return;
  }

  const creationId = libraryItemCreationId(item);
  if (creationId) {
    const isUpload = item.kind === "upload";
    navigate(`/editor?creationId=${creationId}`, {
      state: {
        // Uploads library originals are opaque — run BG remove on open (same as new file upload).
        autoRemoveBackground: isUpload,
        libraryKind: item.kind || null,
      },
    });
  }
}

export function libraryItemDisplayName(item) {
  if (!item) return "Image";
  const raw =
    item.file_name ||
    item.label ||
    item.name ||
    (item.kind === "export" ? `Export ${libraryItemExportId(item) || ""}` : null) ||
    (item.kind === "upload"
      ? `Upload ${libraryItemCreationId(item) || ""}`
      : `Design ${libraryItemCreationId(item) || ""}`);
  return String(raw).replace(/\.[^./\\]+$/, "") || "Image";
}

export function libraryItemFormat(item) {
  const ext =
    item?.input_format ||
    item?.output_format ||
    item?.format ||
    item?.file_ext ||
    "";
  return String(ext).replace(/^\./, "").toUpperCase() || "IMG";
}

function positiveDim(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

/** Pixel size from API fields when present (e.g. 1920 × 1080). */
export function libraryItemPixelSize(item) {
  if (!item) return null;
  const w =
    positiveDim(item.width) ??
    positiveDim(item.output_width) ??
    positiveDim(item.input_width) ??
    positiveDim(item.image_width);
  const h =
    positiveDim(item.height) ??
    positiveDim(item.output_height) ??
    positiveDim(item.input_height) ??
    positiveDim(item.image_height);
  if (!w || !h) return null;
  return `${w} × ${h}`;
}

/** File size label (e.g. 1.2 MB) from API fields when present. */
export function libraryItemFileSize(item) {
  if (!item) return null;
  if (item.file_size_mb != null && item.file_size_mb !== "") {
    const mb = Number(item.file_size_mb);
    if (Number.isFinite(mb) && mb > 0) {
      return mb >= 10 ? `${Math.round(mb)} MB` : `${mb.toFixed(1)} MB`;
    }
  }
  const bytes = Number(item.file_size_bytes ?? item.file_size ?? item.size);
  if (Number.isFinite(bytes) && bytes > 0) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    const mb = bytes / (1024 * 1024);
    return mb >= 10 ? `${Math.round(mb)} MB` : `${mb.toFixed(1)} MB`;
  }
  return null;
}

/**
 * Size shown after format: prefer pixel dimensions, else file size.
 * Optional `measured` = { width, height } from the loaded thumbnail.
 */
export function libraryItemSizeLabel(item, measured = null) {
  const fromApi = libraryItemPixelSize(item);
  if (fromApi) return fromApi;
  const w = positiveDim(measured?.width);
  const h = positiveDim(measured?.height);
  if (w && h) return `${w} × ${h}`;
  return libraryItemFileSize(item);
}

/** Resolution string from API or measured thumbnail. */
export function libraryItemResolutionLabel(item, measured = null) {
  const fromApi = libraryItemPixelSize(item);
  if (fromApi) return fromApi;
  const w = positiveDim(measured?.width);
  const h = positiveDim(measured?.height);
  if (w && h) return `${w} × ${h}`;
  return null;
}

/**
 * Meta line after the name: FORMAT · resolution · file size
 * e.g. "PNG · 1920 × 1080 · 1.2 MB"
 * `measured` may include { width, height }; `measuredBytes` is blob size when API has no file_size.
 */
export function libraryItemMetaLine(item, measured = null, measuredBytes = null) {
  const parts = [libraryItemFormat(item)];
  const resolution = libraryItemResolutionLabel(item, measured);
  if (resolution) parts.push(resolution);
  const fileSize =
    libraryItemFileSize(item) ||
    libraryItemFileSize(
      measuredBytes != null ? { file_size_bytes: measuredBytes } : null
    );
  if (fileSize) parts.push(fileSize);
  return parts.join(" · ");
}
